import React, { FC } from 'react';
import { Select, DatePicker, Input } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AccountModalSelector, Form, LocationInput, TerminalInput } from '~/components';
import { usePath } from '~/hooks';
import EmployeeInput from '~/components/inputs/EmployeeInput';
import { inputWidth, dateWidth } from '~/utils/filters';

const Filters: FC<any> = () => {
  const [form] = Form.useForm();
  const { changePath, params } = usePath();
  const { t } = useTranslation('transactions');
  const tStatus = (key: string): string => t(`status:${key}`);

  const handleChange = (_changed: any, values: Record<string, any>) => {
    const { fromCreatedAt, toCreatedAt, ...rest } = values;
    const filter = {
      fromCreatedAt: fromCreatedAt?.toISOString(),
      toCreatedAt: toCreatedAt?.toISOString(),
      ...rest,
    };
    changePath({ filter });
  };

  const handleInitialValues = (values?: Record<string, any>): Record<string, any> | undefined => {
    if (!values) return;
    const { fromCreatedAt, toCreatedAt, ...rest } = values;
    const filter = {
      fromCreatedAt: fromCreatedAt ? moment(fromCreatedAt) : undefined,
      toCreatedAt: toCreatedAt ? moment(toCreatedAt) : undefined,
      ...rest,
    };
    // eslint-disable-next-line consistent-return
    return filter;
  };

  return (
    <Form
      form={form}
      layout="inline"
      onValuesChange={handleChange}
      initialValues={handleInitialValues(params.filter)}
    >
      <Form.Item name="fromCreatedAt">
        <DatePicker showTime placeholder={t('fromCreatedAt')} style={dateWidth} />
      </Form.Item>
      <Form.Item name="toCreatedAt">
        <DatePicker showTime placeholder={t('toCreatedAt')} style={dateWidth} />
      </Form.Item>
      <Form.Item name="status">
        <Select
          placeholder={t('status')}
          allowClear
          options={[
            { value: 'COMPLETED', label: tStatus('COMPLETED') },
            { value: 'REJECTED', label: tStatus('REJECTED') },
            { value: 'PENDING', label: tStatus('PENDING') },
            { value: 'PROCESSING', label: tStatus('PROCESSING') },
            { value: 'AUTHORIZED', label: tStatus('AUTHORIZED') },
          ]}
          style={inputWidth}
        />
      </Form.Item>
      <Form.Item name="operationType">
        <Select
          placeholder={t('operationType')}
          allowClear
          filterOption={(inputValue, option) =>
            (option?.label as string)?.toUpperCase().indexOf(inputValue?.toUpperCase()) !== -1
          }
          showSearch
          options={[
            { value: 'P2P_TRANSFER', label: t('P2P_TRANSFER') },
            { value: 'PURCHASE', label: t('PURCHASE') },
            { value: 'REFUND', label: t('REFUND') },
            { value: 'BILL_PAYMENT', label: t('BILL_PAYMENT') },
            { value: 'PAYOUT', label: t('PAYOUT') },
            { value: 'TENANT_FEE', label: t('TENANT_FEE') },
            { value: 'TOP_UP', label: t('TOP_UP') },
            { value: 'CASH_DEPOSIT', label: t('CASH_DEPOSIT') },
            { value: 'CASH_WITHDRAWAL', label: t('CASH_WITHDRAWAL') },
            { value: 'OWN_ACCOUNTS_TRANSFER', label: t('OWN_ACCOUNTS_TRANSFER') },
            { value: 'REMITTANCE_PAYOUT', label: t('REMITTANCE_PAYOUT') },
            { value: 'FORCED_APPROVAL', label: t('FORCED_APPROVAL') },
            { value: 'INCOMING_TRANSFER', label: t('INCOMING_TRANSFER') },
            { value: 'ACCOUNT_TRANSFER', label: t('ACCOUNT_TRANSFER') },
            { value: 'REVERSAL', label: t('REVERSAL') },
            { value: 'SERVICE_CHARGE', label: t('SERVICE_CHARGE') },
            { value: 'CASH_COLLECTION', label: t('CASH_COLLECTION') },
            { value: 'CARD_TRANSACTION', label: t('CARD_TRANSACTION') },
            { value: 'DIRECT_TRANSFER', label: t('DIRECT_TRANSFER') },
            { value: 'DEBIT_ORDER', label: t('DEBIT_ORDER') },
          ]}
          style={inputWidth}
        />
      </Form.Item>
      <Form.Item name="terminalId">
        <TerminalInput placeholder={t('terminal')} style={inputWidth} />
      </Form.Item>
      <Form.Item name="locationId">
        <LocationInput placeholder={t('location')} allowClear style={inputWidth} />
      </Form.Item>
      <Form.Item name="employeeId">
        <EmployeeInput placeholder={t('employee')} style={inputWidth} />
      </Form.Item>
      <AccountModalSelector
        selectorForm={form}
        name="accountId"
        selectMode="single"
        label={undefined}
        placeholder={t('account')}
        style={inputWidth}
        onChange={() => {
          changePath({
            filter: {
              ...form.getFieldsValue(),
              accountId: form.getFieldValue('accountId'),
            },
          });
        }}
      />
      <Form.Item name="referenceId">
        <Input placeholder={t('referenceId')} allowClear style={inputWidth} />
      </Form.Item>
    </Form>
  );
};

export default Filters;
